import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ppaUrls } from 'config/urls';
import type {
    PPACreateViewRequest,
    PPACreateViewResponse,
    PPADeleteViewRequest,
    PPADeleteViewResponse,
    PPAGetScenarioViewResponse,
    PPAGetViewListResponse,
    PPAGetViewMetadataRequest,
    PPAGetViewMetadataResponse,
    PPAPersistFilter,
    PPASaveScenarioViewRequest,
    PPASaveScenarioViewResponse,
    PPASaveToViewRequest,
    PPASaveToViewResponse,
    PPASetScenarioActiveLensRequest,
    PPASetScenarioActiveLensResponse,
    PPASetDefaultViewRequest,
    PPASetDefaultViewResponse,
    PPASwitchScenarioViewResponse,
    PPAViewDataSource,
} from 'types/ppaViews';
import { getAuthUserMail } from 'utils/helperMethods';

export const PPA_VIEW_LIST_QUERY_KEY = 'ppa-view-list';
export const PPA_CUSTOM_VIEWS_META_QUERY_KEY = 'ppa-custom-views-meta';

export type PPACustomViewsMeta = {
    config_version?: string;
    quotas?: { customLensLimitPerUser?: number; customViewLimitPerUser?: number };
    view_name_max_length?: number;
};

export async function fetchPpaCustomViewsMeta(): Promise<PPACustomViewsMeta> {
    const response = await axios.get<PPACustomViewsMeta>(ppaUrls.getCustomViewsMetaUrl);
    return response.data;
}

/**
 * Returns the config-driven max length for saved-view names from the
 * `/ppa/meta/custom-views` endpoint. Returns `undefined` while loading or on
 * failure so callers can fall back to their local default.
 */
export function useViewNameMaxLength(): number | undefined {
    const { data } = useQuery({
        queryKey: [PPA_CUSTOM_VIEWS_META_QUERY_KEY],
        queryFn: fetchPpaCustomViewsMeta,
        staleTime: Infinity,
    });
    const fromApi = data?.view_name_max_length;
    return typeof fromApi === 'number' && fromApi > 0 ? fromApi : undefined;
}

export const getPpaViewListQueryKey = (
    scenarioId?: number | null,
    userId?: string | null,
    ppaScopeId?: number | null,
) => [PPA_VIEW_LIST_QUERY_KEY, scenarioId ?? null, userId ?? null, ppaScopeId ?? null] as const;

export async function fetchPpaViewList(scenarioId?: number | null): Promise<PPAGetViewListResponse> {
    const response = await axios.post<PPAGetViewListResponse>(ppaUrls.getViewListUrl, {
        scenario_id: scenarioId ?? null,
        user_id: getAuthUserMail() || null,
    });
    return response.data;
}

export async function fetchPpaViewMetadata(
    viewId: number,
    options?: {
        scenarioId?: number | null;
        dataSource?: PPAViewDataSource;
        searchKeyword?: string | null;
    },
): Promise<PPAGetViewMetadataResponse> {
    const searchKeyword = options?.searchKeyword?.trim();
    const response = await axios.post<PPAGetViewMetadataResponse>(ppaUrls.getViewMetadataUrl, {
        view_id: viewId,
        scenario_id: options?.scenarioId ?? null,
        data_source: options?.dataSource ?? 'view',
        user_id: getAuthUserMail() || null,
        ...(searchKeyword ? { search_keyword: searchKeyword } : {}),
    } satisfies PPAGetViewMetadataRequest);
    return response.data;
}

export async function fetchPpaScenarioView(scenarioId: number): Promise<PPAGetScenarioViewResponse> {
    const response = await axios.post<PPAGetScenarioViewResponse>(ppaUrls.getScenarioViewUrl, {
        scenario_id: scenarioId,
        user_id: getAuthUserMail() || null,
    });
    return response.data;
}

export async function saveScenarioDeviation(payload: PPASaveScenarioViewRequest): Promise<PPASaveScenarioViewResponse> {
    const response = await axios.post<PPASaveScenarioViewResponse>(ppaUrls.saveScenarioViewUrl, payload);
    return response.data;
}

export async function savePpaToView(
    scenarioId: number,
    options: { baseViewUpdatedAt: string; persistFilter?: PPAPersistFilter },
): Promise<PPASaveToViewResponse> {
    const response = await axios.post<PPASaveToViewResponse>(ppaUrls.saveToViewUrl, {
        scenario_id: scenarioId,
        user_id: getAuthUserMail() || undefined,
        base_view_updated_at: options.baseViewUpdatedAt,
        ...(options.persistFilter ? { persist_filter: options.persistFilter } : {}),
    } satisfies PPASaveToViewRequest);
    return response.data;
}

export async function switchPpaScenarioView(
    scenarioId: number,
    viewId: number,
): Promise<PPASwitchScenarioViewResponse> {
    const response = await axios.post<PPASwitchScenarioViewResponse>(ppaUrls.switchScenarioViewUrl, {
        scenario_id: scenarioId,
        view_id: viewId,
        user_id: getAuthUserMail() || null,
    });
    return response.data;
}

export async function setScenarioActiveLens(
    scenarioId: number,
    lensId: number,
): Promise<PPASetScenarioActiveLensResponse> {
    const response = await axios.post<PPASetScenarioActiveLensResponse>(ppaUrls.setScenarioActiveLensUrl, {
        scenario_id: scenarioId,
        lens_id: lensId,
        user_id: getAuthUserMail() || null,
    } satisfies PPASetScenarioActiveLensRequest);
    return response.data;
}

/** Resolves the view for a scenario, then loads its metadata. Returns null when no view is assigned. */
export async function fetchPpaLoadedViewMetadata(scenarioId: number): Promise<PPAGetViewMetadataResponse | null> {
    const scenarioView = await fetchPpaScenarioView(scenarioId);
    if (!scenarioView.view_id) {
        return null;
    }
    return fetchPpaViewMetadata(scenarioView.view_id);
}

export async function createPpaView(payload: PPACreateViewRequest): Promise<PPACreateViewResponse> {
    const response = await axios.post<PPACreateViewResponse>(ppaUrls.createViewUrl, payload);
    return response.data;
}

export async function deletePpaView(payload: PPADeleteViewRequest): Promise<PPADeleteViewResponse> {
    const response = await axios.post<PPADeleteViewResponse>(ppaUrls.deleteViewUrl, payload);
    return response.data;
}

export async function setDefaultPpaView(
    payload: PPASetDefaultViewRequest,
): Promise<PPASetDefaultViewResponse> {
    const response = await axios.post<PPASetDefaultViewResponse>(ppaUrls.setDefaultViewUrl, {
        is_default: true,
        ...payload,
        user_id: getAuthUserMail() || null,
    });
    return response.data;
}

export function usePpaViewMutations() {
    const queryClient = useQueryClient();

    const invalidateViewList = () => queryClient.invalidateQueries({ queryKey: [PPA_VIEW_LIST_QUERY_KEY] });

    const createViewMutation = useMutation({
        mutationFn: createPpaView,
    });

    const deleteViewMutation = useMutation({
        mutationFn: deletePpaView,
        onSuccess: invalidateViewList,
    });

    const setDefaultViewMutation = useMutation({
        mutationFn: setDefaultPpaView,
        onSuccess: invalidateViewList,
    });

    return {
        createViewMutation,
        deleteViewMutation,
        setDefaultViewMutation,
        fetchViewMetadata: fetchPpaViewMetadata,
    };
}
